import { useState } from "react";
import { SITE_URL } from "@/config/site";
import { PLAN_PRICES } from "@/data/plans";
import { CONTACT_EMAIL } from "@/data/contact";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, X, Minus, ExternalLink, Info } from "lucide-react";

/**
 * Public comparison page — Stronger Bot vs other AI platforms.
 *
 * Juridiquement safe:
 * - Seuls des faits publics et vérifiables (prix affichés sur leur site).
 * - Pas de logo concurrent (risque trademark).
 * - Ton factuel, pas de "meilleur que X" (évite dénigrement — art. L. 121-8
 *   Code consommation FR / Directive 2006/114/CE sur publicité comparative).
 * - Source et date de relevé des prix clairement indiqués.
 * - Mention "sous réserve de modification".
 * - Lien vers le site officiel du concurrent pour que l'utilisateur puisse
 *   vérifier.
 *
 * i18n: tout le texte affiché passe par le namespace `compare.*` (15 langues,
 * public/locales/*). Les données factuelles (prix chiffrés, statut Oui/Non par
 * concurrent) restent en dur ici — seuls les libellés traduisibles référencent
 * une clé i18n. Pour ajouter un concurrent : entrée COMPETITORS + valeurs par
 * row. Pour ajouter un axe : entrée ROWS avec `catKey` + `labelKey` existants
 * dans `compare.cat.*` / `compare.lbl.*`.
 */

const COMPARISON_DATE = "2026-09";

// Concurrents. `name` = marque (littéral, jamais traduit). Les libellés de prix
// numériques restent littéraux (neutres) ; les mentions traduisibles pointent
// vers une clé i18n (`annualHintKey`, `sourceNoteKey`).
// Prix Pro grand public relevés en septembre 2026 sur les sites officiels
// (voir COMPARISON_DATE + le disclaimer). Ordre : nous d'abord, puis les
// concurrents les plus recherchés ("alternative à …"), Genspark en dernier.
const COMPETITORS = [
  {
    key: "strongerbot",
    name: "Stronger Bot",
    priceProMonthly: `€${PLAN_PRICES.pro}`,
    annualHintKey: "trialAnnual",
    website: null,
    ours: true,
  },
  {
    key: "chatgpt",
    name: "ChatGPT",
    priceProMonthly: "$20",
    website: "https://openai.com/chatgpt/pricing/",
    ours: false,
  },
  {
    key: "claude",
    name: "Claude",
    priceProMonthly: "$20",
    website: "https://www.anthropic.com/pricing",
    ours: false,
  },
  {
    key: "perplexity",
    name: "Perplexity",
    priceProMonthly: "$20",
    website: "https://www.perplexity.ai/",
    ours: false,
  },
  {
    key: "gemini",
    name: "Google Gemini",
    priceProMonthly: "$19.99",
    website: "https://gemini.google.com/",
    ours: false,
  },
  {
    key: "poe",
    name: "Poe",
    priceProMonthly: "$19.99",
    annualHintKey: "poePointsHint",
    website: "https://poe.com/",
    ours: false,
  },
  {
    key: "genspark",
    name: "Genspark",
    priceProMonthly: "$199.99",
    annualHintKey: "gsPlusHint",
    website: "https://www.genspark.ai/",
    sourceNoteKey: "sourceNote",
    ours: false,
  },
];

// Default competitor shown in the SB-vs-X selector (highest "alternative to"
// search intent). Overridable by the ?vs= query param.
const DEFAULT_COMPETITOR = "chatgpt";

// Statuts factuels — la clé `status` est stable (jamais traduite) ; le libellé
// affiché vient de `compare.status.<status>` (ou d'un `cellKey` d'override).
const YES = { status: "yes", icon: Check, cls: "text-green-500" };
const NO = { status: "no", icon: X, cls: "text-slate-400" };
const PARTIAL = { status: "partial", icon: Minus, cls: "text-yellow-500" };

// Chaque row = un axe factuel. `catKey`/`labelKey` référencent compare.cat.* /
// compare.lbl.*. Une valeur peut porter :
//   - un statut (YES/NO/PARTIAL) éventuellement + `cellKey` (override de libellé)
//   - `custom` (chiffre/marque littéral) + `hintKey` (mention traduisible) ou
//     `perMonth: true` (suffixe « /mois » traduit)
const ROWS = [
  {
    catKey: "pricing",
    labelKey: "priceProMonthly",
    values: {
      strongerbot: { custom: `€${PLAN_PRICES.pro}`, hintKey: "pro" },
      genspark: { custom: "$199.99", hintKey: "pro" },
    },
  },
  {
    catKey: "pricing",
    labelKey: "entryPaid",
    values: {
      strongerbot: {
        custom: `€${PLAN_PRICES.starter}`,
        perMonth: true,
        hintKey: "starter",
      },
      genspark: { custom: "$19.99", perMonth: true, hintKey: "plus" },
    },
  },
  {
    catKey: "pricing",
    labelKey: "freeVersion",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "pricing",
    labelKey: "freeTrial",
    values: {
      strongerbot: { ...YES, cellKey: "days7" },
      genspark: PARTIAL,
    },
  },
  {
    // Usage non plafonné vs facturation à l'usage (points/crédits). Axe
    // factuel différenciant : SB vend un forfait à usage illimité ; les
    // plateformes à points (Poe) ou à crédits facturent chaque message.
    catKey: "pricing",
    labelKey: "unlimitedUsage",
    values: {
      strongerbot: { ...YES, cellKey: "unlimited" },
      genspark: { ...PARTIAL, cellKey: "credits" },
    },
  },
  {
    // Sans publicité. ChatGPT a introduit de la pub dans son free tier
    // (89 marchés, 10/2026) ; SB n'affiche aucune pub. Axe factuel, honnête —
    // on crédite les rivaux sans pub (Claude/Gemini/Poe/Genspark).
    catKey: "pricing",
    labelKey: "adFree",
    values: {
      strongerbot: YES,
      genspark: YES,
    },
  },
  {
    catKey: "models",
    labelKey: "modelSelector",
    values: {
      strongerbot: { ...YES, cellKey: "models10" },
      genspark: NO,
    },
  },
  {
    catKey: "models",
    labelKey: "multiProvider",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "models",
    labelKey: "openSource",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "research",
    labelKey: "deepResearch",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "research",
    labelKey: "seoPublic",
    values: {
      strongerbot: { ...PARTIAL, cellKey: "roadmap" },
      genspark: YES,
    },
  },
  {
    catKey: "research",
    labelKey: "docAnalysis",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "research",
    labelKey: "youtube",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "agents",
    labelKey: "configAgents",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "agents",
    labelKey: "integrations",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "agents",
    labelKey: "publicApi",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "media",
    labelKey: "imageGen",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "media",
    labelKey: "tts",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "media",
    labelKey: "stt",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "media",
    labelKey: "videoGen",
    values: {
      strongerbot: { ...PARTIAL, cellKey: "roadmap" },
      genspark: YES,
    },
  },
  {
    catKey: "intl",
    labelKey: "multilingual",
    values: {
      strongerbot: { custom: null, cellKey: "langs15" },
      genspark: { custom: null, cellKey: "mainlyEn" },
    },
  },
  {
    catKey: "intl",
    labelKey: "frenchNative",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "intl",
    labelKey: "arabic",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "platforms",
    labelKey: "webPwa",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "platforms",
    labelKey: "mobileApp",
    values: {
      strongerbot: { ...PARTIAL, cellKey: "soon" },
      genspark: YES,
    },
  },
  {
    catKey: "enterprise",
    labelKey: "ssoScim",
    values: { strongerbot: YES, genspark: PARTIAL },
  },
  {
    catKey: "enterprise",
    labelKey: "teams",
    values: { strongerbot: YES, genspark: YES },
  },
  {
    catKey: "enterprise",
    labelKey: "whiteLabel",
    values: { strongerbot: YES, genspark: NO },
  },
];

// Valeurs par concurrent, indexées par `labelKey` (unique cross-catégorie).
// Injectées dans ROWS.values ci-dessous — évite de réécrire chaque row.
// Faits publics grand public constatés 09/2026 ; conservateur (PARTIAL quand
// c'est limité/variable), et on CRÉDITE honnêtement les concurrents là où la
// fonctionnalité existe (pas de dénigrement — cf. en-tête juridique). Le vrai
// axe différenciant de SB = 1 abo → plusieurs fournisseurs + modèles
// open-weight + 15 langues dont arabe first-class, pas "eux ne savent pas".
const COMPETITOR_VALUES = {
  chatgpt: {
    adFree: NO, // pub introduite dans le free tier (89 marchés, 10/2026)
    priceProMonthly: { custom: "$20", hintKey: "pro" },
    entryPaid: { custom: "$20", perMonth: true, hintKey: "plus" },
    freeVersion: YES,
    freeTrial: PARTIAL,
    unlimitedUsage: { ...PARTIAL, cellKey: "caps" },
    modelSelector: PARTIAL,
    multiProvider: NO,
    openSource: NO,
    deepResearch: YES,
    seoPublic: NO,
    docAnalysis: YES,
    youtube: PARTIAL,
    configAgents: YES,
    integrations: YES,
    publicApi: YES,
    imageGen: YES,
    tts: YES,
    stt: YES,
    videoGen: PARTIAL,
    multilingual: { custom: null, cellKey: "manyLangs" },
    frenchNative: YES,
    arabic: PARTIAL,
    webPwa: YES,
    mobileApp: YES,
    ssoScim: YES,
    teams: YES,
    whiteLabel: NO,
  },
  claude: {
    adFree: YES,
    priceProMonthly: { custom: "$20", hintKey: "pro" },
    entryPaid: { custom: "$20", perMonth: true, hintKey: "pro" },
    freeVersion: YES,
    freeTrial: PARTIAL,
    unlimitedUsage: { ...PARTIAL, cellKey: "caps" },
    modelSelector: PARTIAL,
    multiProvider: NO,
    openSource: NO,
    deepResearch: YES,
    seoPublic: NO,
    docAnalysis: YES,
    youtube: NO,
    configAgents: PARTIAL,
    integrations: PARTIAL,
    publicApi: YES,
    imageGen: NO,
    tts: PARTIAL,
    stt: PARTIAL,
    videoGen: NO,
    multilingual: { custom: null, cellKey: "manyLangs" },
    frenchNative: YES,
    arabic: PARTIAL,
    webPwa: YES,
    mobileApp: YES,
    ssoScim: YES,
    teams: YES,
    whiteLabel: NO,
  },
  perplexity: {
    adFree: PARTIAL, // questions sponsorisées (ads) dans le free tier
    priceProMonthly: { custom: "$20", hintKey: "pro" },
    entryPaid: { custom: "$20", perMonth: true, hintKey: "pro" },
    freeVersion: YES,
    freeTrial: PARTIAL,
    unlimitedUsage: { ...PARTIAL, cellKey: "caps" },
    modelSelector: YES,
    multiProvider: YES,
    openSource: PARTIAL,
    deepResearch: YES,
    seoPublic: NO,
    docAnalysis: YES,
    youtube: PARTIAL,
    configAgents: PARTIAL,
    integrations: PARTIAL,
    publicApi: YES,
    imageGen: PARTIAL,
    tts: YES,
    stt: YES,
    videoGen: NO,
    multilingual: { custom: null, cellKey: "manyLangs" },
    frenchNative: YES,
    arabic: PARTIAL,
    webPwa: YES,
    mobileApp: YES,
    ssoScim: PARTIAL,
    teams: YES,
    whiteLabel: NO,
  },
  gemini: {
    adFree: YES,
    priceProMonthly: { custom: "$19.99", hintKey: "pro" },
    entryPaid: { custom: "$19.99", perMonth: true, hintKey: "pro" },
    freeVersion: YES,
    freeTrial: PARTIAL,
    unlimitedUsage: { ...PARTIAL, cellKey: "caps" },
    modelSelector: PARTIAL,
    multiProvider: NO,
    openSource: NO,
    deepResearch: YES,
    seoPublic: NO,
    docAnalysis: YES,
    youtube: YES,
    configAgents: PARTIAL,
    integrations: YES,
    publicApi: YES,
    imageGen: YES,
    tts: YES,
    stt: YES,
    videoGen: PARTIAL,
    multilingual: { custom: null, cellKey: "manyLangs" },
    frenchNative: YES,
    arabic: PARTIAL,
    webPwa: YES,
    mobileApp: YES,
    ssoScim: YES,
    teams: YES,
    whiteLabel: NO,
  },
  // Poe (Quora). Faits publics 2026 : très large choix de modèles (dont
  // frontier) + apps natives + médias — crédités honnêtement. Facturation à
  // l'usage par POINTS (chaque message consomme des points selon le modèle) :
  // reflété sur l'axe `unlimitedUsage`. Prix : Starter $4.99 / Premium $19.99.
  poe: {
    adFree: YES,
    priceProMonthly: { custom: "$19.99", hintKey: "premium" },
    entryPaid: { custom: "$4.99", perMonth: true, hintKey: "starter" },
    freeVersion: YES,
    freeTrial: PARTIAL,
    unlimitedUsage: { ...PARTIAL, cellKey: "points" },
    modelSelector: YES,
    multiProvider: YES,
    openSource: PARTIAL,
    deepResearch: YES,
    seoPublic: NO,
    docAnalysis: YES,
    youtube: PARTIAL,
    configAgents: YES,
    integrations: PARTIAL,
    publicApi: YES,
    imageGen: YES,
    tts: YES,
    stt: PARTIAL,
    videoGen: YES,
    multilingual: { custom: null, cellKey: "manyLangs" },
    frenchNative: PARTIAL,
    arabic: PARTIAL,
    webPwa: YES,
    mobileApp: YES,
    ssoScim: PARTIAL,
    teams: YES,
    whiteLabel: NO,
  },
};
ROWS.forEach((r) => {
  Object.keys(COMPETITOR_VALUES).forEach((key) => {
    if (r.values[key] === undefined)
      r.values[key] = COMPETITOR_VALUES[key][r.labelKey] ?? PARTIAL;
  });
});

// Ordre d'affichage des catégories (clés stables → libellé via compare.cat.*).
const CATEGORY_ORDER = [
  "pricing",
  "models",
  "research",
  "agents",
  "media",
  "intl",
  "platforms",
  "enterprise",
];

const ComparePage = () => {
  const { t } = useTranslation();

  // SB-vs-X selector: SB is always the reference; the user picks one competitor
  // at a time (keeps the layout 2-column = mobile-first, §E 375px, and reads as
  // "Stronger Bot vs ChatGPT" — the high-intent "alternative to X" framing).
  // Deep-linkable via ?vs=<key>; canonical stays /compare.
  const [searchParams] = useSearchParams();
  const paramKey = searchParams.get("vs");
  const validParam = COMPETITORS.some((c) => c.key === paramKey && !c.ours);
  const [selectedKey, setSelectedKey] = useState(
    validParam ? paramKey : DEFAULT_COMPETITOR
  );
  const rivals = COMPETITORS.filter((c) => !c.ours);
  const shown = COMPETITORS.filter((c) => c.ours || c.key === selectedKey);

  // Structured data — BreadcrumbList + FAQPage. Client-injected (same pattern as
  // the landing @graph, prerender note). Targets FAQ rich results on the
  // high-intent "alternative to X" queries this page ranks for. Q/A come from
  // i18n (compare.faq.*, 15/15) so each locale emits its own localised FAQ.
  const faq = [1, 2, 3, 4].map((n) => ({
    "@type": "Question",
    name: t(`compare.faq.q${n}`),
    acceptedAnswer: { "@type": "Answer", text: t(`compare.faq.a${n}`) },
  }));
  const compareJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t("compare.crumbHome", "Accueil"),
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t(
              "compare.title",
              "Stronger Bot face aux autres assistants IA"
            ),
            item: `${SITE_URL}/compare`,
          },
        ],
      },
      { "@type": "FAQPage", mainEntity: faq },
    ],
  };

  const renderCell = (value, isOurs) => {
    if (!value) return <Minus className="w-5 h-5 text-slate-600 mx-auto" />;

    // Cellule "custom" : chiffre/marque littéral (+ suffixe /mois + hint).
    if (value.custom || (value.custom === null && value.cellKey)) {
      const text = value.custom
        ? value.perMonth
          ? `${value.custom}${t("compare.perMonth", "/mois")}`
          : value.custom
        : t(`compare.cell.${value.cellKey}`, value.cellKey);
      const hint = value.hintKey
        ? t(`compare.hint.${value.hintKey}`, value.hintKey)
        : null;
      return (
        <div
          className={isOurs ? "text-primary font-semibold" : "text-slate-200"}
        >
          {text}
          {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
        </div>
      );
    }

    // Cellule statut : icône + libellé (status ou override cellKey).
    const meta =
      value.status === "yes" ? YES : value.status === "no" ? NO : PARTIAL;
    const Icon = meta.icon;
    const label = value.cellKey
      ? t(`compare.cell.${value.cellKey}`, value.cellKey)
      : t(`compare.status.${meta.status}`, meta.status);
    return (
      <div className="flex flex-col items-center gap-1">
        <Icon className={`w-5 h-5 ${meta.cls}`} />
        <span className="text-xs text-slate-400">{label}</span>
      </div>
    );
  };

  const groupedRows = CATEGORY_ORDER.map((catKey) => ({
    catKey,
    rows: ROWS.filter((r) => r.catKey === catKey),
  })).filter((g) => g.rows.length > 0);

  return (
    <div className="min-h-screen bg-background text-white">
      <SEO
        title={t(
          "compare.seoTitle",
          "Comparaison des assistants IA — Stronger Bot"
        )}
        description={t(
          "compare.seoDescription",
          "Tableau comparatif factuel entre Stronger Bot et d'autres assistants IA. Prix publics, fonctionnalités, intégrations — mis à jour régulièrement."
        )}
        canonical="/compare"
        jsonLd={compareJsonLd}
      />

      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="compare-back-to-pricing"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("compare.backToPricing", "Retour aux tarifs")}
        </Link>

        <header className="mb-10">
          <h1
            className="font-heading text-3xl md:text-5xl font-bold mb-4"
            data-testid="compare-title"
          >
            {t("compare.title", "Stronger Bot face aux autres assistants IA")}
          </h1>
          <p className="text-slate-400 text-lg max-w-3xl">
            {t(
              "compare.intro",
              "Un tableau factuel qui aide à choisir. Nous listons les fonctionnalités publiques de chaque plateforme, au prix affiché sur leur site. À toi de juger."
            )}
          </p>
        </header>

        {/* Disclaimer juridique — visible d'emblée */}
        <div
          className="glass rounded-2xl p-5 mb-8 border border-slate-700 bg-slate-900/40"
          data-testid="compare-disclaimer"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-300 space-y-2">
              <p>
                <strong>
                  {t("compare.disclaimerTitle", "Mentions et sources")}
                </strong>{" "}
                —{" "}
                {t(
                  "compare.disclaimerBody",
                  "Prix publics et fonctionnalités constatés"
                )}{" "}
                <strong>{COMPARISON_DATE}</strong>{" "}
                {t(
                  "compare.disclaimerBody2",
                  "sur les sites officiels des éditeurs. Les tarifs et périmètres fonctionnels sont susceptibles d'évoluer à tout moment. Ce tableau n'exprime aucun jugement sur la qualité ; à chacun de vérifier auprès de la source officielle et d'évaluer selon ses besoins."
                )}
              </p>
              <p className="text-slate-400">
                {t(
                  "compare.disclaimerNominative",
                  "Les noms de produits cités appartiennent à leurs propriétaires respectifs et sont utilisés à titre purement descriptif, sans affiliation ni endossement."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Sources */}
        <div
          className="flex flex-wrap gap-3 mb-10"
          data-testid="compare-sources"
        >
          {shown
            .filter((c) => c.website)
            .map((c) => (
              <a
                key={c.key}
                href={c.website}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-primary bg-slate-900/50 rounded-lg px-3 py-2 border border-slate-800"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t("compare.officialSite", "Site officiel")} {c.name}
              </a>
            ))}
        </div>

        {/* Competitor selector — SB stays the reference, pick one to compare */}
        <div className="mb-6" data-testid="compare-selector">
          <p className="text-sm text-slate-400 mb-3">
            {t("compare.selectLabel", "Comparer Stronger Bot avec :")}
          </p>
          <div className="flex flex-wrap gap-2">
            {rivals.map((c) => {
              const active = c.key === selectedKey;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setSelectedKey(c.key)}
                  aria-pressed={active}
                  data-testid={`compare-chip-${c.key}`}
                  className={`min-h-[44px] rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                    active
                      ? "bg-primary text-white border-primary"
                      : "bg-slate-900/50 text-slate-300 border-slate-700 hover:border-primary/50 hover:text-white"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Header cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 sticky top-0 z-10 pt-2 pb-4 bg-background/80 backdrop-blur-sm">
          <div className="hidden md:block" />
          {shown.map((c) => (
            <div
              key={c.key}
              className={`rounded-xl p-4 border text-center ${
                c.ours
                  ? "border-primary/50 bg-primary/10"
                  : "border-slate-800 bg-slate-900/50"
              }`}
              data-testid={`compare-col-${c.key}`}
            >
              <div className="font-heading text-lg font-semibold">
                {c.name}
                {c.ours && (
                  <span className="ml-2 text-xs bg-primary/20 text-foreground px-2 py-0.5 rounded">
                    {t("compare.thisProduct", "Nous")}
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold mt-2">
                {c.priceProMonthly}
                <span className="text-base font-normal text-slate-400">
                  {t("compare.perMonth", "/mois")}
                </span>
              </div>
              {c.annualHintKey && (
                <div className="text-xs text-slate-400 mt-1">
                  {t(`compare.${c.annualHintKey}`, c.annualHintKey)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Comparison table — grouped by category */}
        <div className="space-y-8">
          {groupedRows.map(({ catKey, rows }) => (
            <section
              key={catKey}
              className="glass rounded-2xl overflow-hidden"
              data-testid={`compare-section-${catKey}`}
            >
              <h2 className="font-heading text-lg font-semibold px-5 py-3 bg-slate-900/60 border-b border-slate-800">
                {t(`compare.cat.${catKey}`, catKey)}
              </h2>
              <div className="divide-y divide-slate-800">
                {rows.map((row) => (
                  <div
                    key={row.labelKey}
                    data-testid={`compare-row-${row.labelKey}`}
                    className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 px-5 py-4 items-center"
                  >
                    <div className="col-span-2 md:col-span-1 text-sm text-slate-300">
                      {t(`compare.lbl.${row.labelKey}`, row.labelKey)}
                    </div>
                    {shown.map((c) => (
                      <div
                        key={c.key}
                        className={`text-center text-sm ${
                          c.ours ? "bg-primary/5 rounded-md py-2" : ""
                        }`}
                        data-testid={`compare-cell-${c.key}`}
                      >
                        {renderCell(row.values[c.key], c.ours)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-10 glass rounded-2xl p-5">
          <h3 className="font-semibold mb-3">
            {t("compare.legendTitle", "Légende")}
          </h3>
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />{" "}
              {t("compare.legendYes", "Disponible")}
            </span>
            <span className="flex items-center gap-2">
              <Minus className="w-4 h-4 text-yellow-500" />{" "}
              {t("compare.legendPartial", "Partiel ou en roadmap")}
            </span>
            <span className="flex items-center gap-2">
              <X className="w-4 h-4 text-slate-400" />{" "}
              {t("compare.legendNo", "Non disponible")}
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link to="/register">
            <Button
              size="lg"
              className="bg-primary-strong hover:bg-primary-strong/90 px-8 text-base font-semibold"
              data-testid="compare-cta-register"
            >
              {t("compare.ctaRegister", "Essayer Stronger Bot gratuitement")}
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-3">
            {t("compare.ctaHint", "Aucune carte requise pour le plan gratuit.")}
          </p>
        </div>

        {/* Footer legal disclaimer */}
        <div className="mt-12 pt-6 border-t border-slate-800 text-xs text-slate-400 space-y-2">
          <p>
            {t(
              "compare.footerLegal1",
              "Comparatif publié à titre informatif. Les données chiffrées proviennent des pages publiques des éditeurs cités à la date indiquée. Stronger Bot ne prétend pas couvrir toutes les fonctionnalités de ses concurrents et invite chacun à consulter les sources officielles pour une analyse complète."
            )}
          </p>
          <p>
            {t(
              "compare.footerLegal2",
              "Les noms et marques cités sont la propriété de leurs détenteurs respectifs. Aucune affiliation n'est suggérée. Pour toute inexactitude : {{email}}.",
              { email: CONTACT_EMAIL }
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComparePage;
